import { Box, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/common/Button';
import Loading from '../../../components/common/Loading';
import SelectField from '../../../components/common/SelectField';
import TextField from '../../../components/common/TextField';
import LinkedinIcon from '../../../components/common/icons/LinkedinIcon';
import CustomTable from '../../../components/common/table/CustomTable';
import { Column } from '../../../components/common/table/types';
import ProfileDetailsPopup from '../../../components/module/rec/linkedin/ProfileDetailsPopup';
import { usePermissions } from '../../../context/PermissionContext';
import { API_HEADERS } from '../../../constants/apiHeaders';
import { APP_ROUTES } from '../../../constants/routes';
import { PAGE_IDS, PERMISSION_IDS } from '../../../constants/permissions';
import { useSidebar } from '../../../context/SidebarContext';
import api from '../../../services/httpClient';
import { PROFILE_ENDPOINTS } from '../../../services/endpoints/rec/profile.endpoints';
import { formatDate } from '../../../utils/date';
import { DEFAULT_ROWS_PER_PAGE } from '../../../utils/constants';

interface LinkedinProfile {
  id: number;
  name: string;
  company: string;
  experience: number;
  vacancy: string;
  email: string;
  location: string;
  synced_date: string;
  addedBy: string;
  addedByName: string;
  profileUrl: string;
  designation: string;
  elapsedTime: number;
  applicantId: number;
}

interface LinkedinProfileResponse {
  count: number;
  data: LinkedinProfile[];
}

interface FetchLinkedinProfilesParams {
  name?: string;
  skills?: string;
  company?: string;
  experience?: string;
  vacancy?: string;
  email?: string;
  country?: string;
  synced_date?: string;
  added_by?: string;
  page: number;
  limit: number;
  sort?: string;
  isOnlyWithEmail?: string;
}

const LinkedinSearch: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const { updateMenu } = useSidebar();
  const [open, setOpen] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const { hasPermission, pageId, setPageId } = usePermissions();
  const [isOnlyWithEmail, setIsOnlyWithEmail] = useState(false);
  const [profiles, setProfiles] = useState<LinkedinProfile[]>([]);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [selectedProfileId, setSelectedProfileId] = useState<number | null>(null);
  const [sortField, setSortField] = useState<keyof LinkedinProfile | undefined>('synced_date');
  const [searchParams, setSearchParams] = useState({
    name: '',
    skills: '',
    company: '',
    experience: '',
    vacancy: '',
    email: '',
    country: '',
    synced_date: '',
    added_by: '',
  });
  const [filterParams, setFilterParams] = useState({
    name: '',
    skills: '',
    company: '',
    experience: '',
    vacancy: '',
    email: '',
    country: '',
    synced_date: '',
    added_by: '',
  });

  const fetchLinkedinProfiles = useCallback(async (params: FetchLinkedinProfilesParams) => {
    try {
      setIsLoading(true);
      const response = await api.get<LinkedinProfileResponse>(
        PROFILE_ENDPOINTS.search,
        {
          params: {
            ...params,
            sort: sortField ? `${sortField}:${sortDirection}` : undefined
          },
          headers: { ...API_HEADERS.LINKEDIN_PROFILE_READ },
        }
      );
      setProfiles(response.data.data ? response.data.data : []);
      setTotalCount(response.data.count);
    } catch (error) {
      console.error('Error fetching LinkedIn profiles:', error);
      setProfiles([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [sortField, sortDirection]);

  useEffect(() => {
    if (pageId !== PAGE_IDS.LINKEDIN_SEARCH) {
      setPageId(PAGE_IDS.LINKEDIN_SEARCH);
      return;
    }

    fetchLinkedinProfiles({
      name: filterParams.name ? filterParams.name : undefined,
      skills: filterParams.skills ? filterParams.skills : undefined,
      company: filterParams.company ? filterParams.company : undefined,
      experience: filterParams.experience ? filterParams.experience : undefined,
      vacancy: filterParams.vacancy ? filterParams.vacancy : undefined,
      email: filterParams.email ? filterParams.email : undefined,
      country: filterParams.country ? filterParams.country : undefined,
      synced_date: filterParams.synced_date ? filterParams.synced_date : undefined,
      added_by: filterParams.added_by ? filterParams.added_by : undefined,
      isOnlyWithEmail: isOnlyWithEmail ? 'true' : 'false',
      page: page + 1,
      limit: rowsPerPage
    });
  }, [
    page,
    rowsPerPage,
    sortField,
    sortDirection,
    filterParams.name,
    filterParams.skills,
    filterParams.company,
    filterParams.experience,
    filterParams.vacancy,
    filterParams.email,
    filterParams.country,
    filterParams.synced_date,
    filterParams.added_by,
    isOnlyWithEmail,
    pageId,
    setPageId,
    fetchLinkedinProfiles,
  ]);

  const handleSearch = () => {
    setPage(0); // Reset to first page when searching
    setFilterParams({
      name: searchParams.name,
      skills: searchParams.skills,
      company: searchParams.company,
      experience: searchParams.experience,
      vacancy: searchParams.vacancy,
      email: searchParams.email,
      country: searchParams.country,
      synced_date: searchParams.synced_date,
      added_by: searchParams.added_by,
    });
  };

  const handleReset = () => {
    setSearchParams({
      name: '',
      skills: '',
      company: '',
      experience: '',
      vacancy: '',
      email: '',
      country: '',
      synced_date: '',
      added_by: '',
    });
    setFilterParams({
      name: '',
      skills: '',
      company: '',
      experience: '',
      vacancy: '',
      email: '',
      country: '',
      synced_date: '',
      added_by: '',
    });
    setPage(0);
    setIsOnlyWithEmail(false);
  };

  const handleSort = (field: keyof LinkedinProfile) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleAddApplicant = useCallback((profile: LinkedinProfile) => {
    setPageId(PAGE_IDS.APPLICANT_ADD);
    navigate(APP_ROUTES.APPLICANT.ADD, {
      state: {
        initialData: {
          vacancyCode: profile.vacancy,
          vacancy: profile.vacancy,
          source: 'linkedin',
          id: profile.id
        }
      }
    });
    updateMenu(2);
  }, [navigate, setPageId, updateMenu]);

  const columns: Column<LinkedinProfile>[] = useMemo(() =>
    [
      {
        field: 'name' as keyof LinkedinProfile, headerName: 'Name', width: '220px', sortable: true, type: 'text',
        renderCell: (row: LinkedinProfile) => (
          <div onClick={() => {
            setSelectedProfileId(row.id);
            setOpen(true);
          }} style={{ cursor: 'pointer' }}>{row.name}</div>
        )
      },
      { field: 'designation' as keyof LinkedinProfile, headerName: 'Designation', width: '150px', sortable: true, type: 'text' },
      { field: 'company' as keyof LinkedinProfile, headerName: 'Company', width: '150px', sortable: true, type: 'text' },
      {
        field: 'experience' as keyof LinkedinProfile, headerName: 'Experience', width: '80px', sortable: true, type: 'text',
        renderCell: (row: LinkedinProfile) => {
          return row.experience > 0 ? `${Math.floor(row.experience / 12)} Yrs ${row.experience % 12} Mos` : '';
        }
      },
      { field: 'vacancy' as keyof LinkedinProfile, headerName: 'Vacancy', width: '170px', sortable: true, type: 'text' },
      {
        field: 'email' as keyof LinkedinProfile,
        headerName: 'Email',
        width: '150px',
        sortable: true,
        type: 'text',
        checkable: true,
        isChecked: isOnlyWithEmail,
        onCheck: (checked) => setIsOnlyWithEmail(checked),
      },
      {
        field: 'location' as keyof LinkedinProfile, headerName: 'Country', width: '80px', sortable: true, type: 'text',
        renderCell: (row: LinkedinProfile) => {
          const location = row.location?.split(',') ?? [];
          return location.length > 0 ? location[location.length - 1] : '';
        }
      },
      { field: 'synced_date' as keyof LinkedinProfile, headerName: 'Synced', width: '100px', sortable: true, type: 'date', renderCell: (row) => formatDate(row.synced_date) },
      {
        field: 'elapsedTime',
        headerName: 'Elapsed Time',
        sortable: true,
        width: '40x',
        type: 'text',
        renderCell: (row) => {
          const elapsedTime = `${row.elapsedTime} ${row.elapsedTime > 1 ? 'days' : 'day'}`;
          return (
            <Box display="flex" gap="5px" alignItems="center">
              <div style={{ width: '6px', height: '6px', backgroundColor: `${!!row.applicantId ? '#0D9C33' : '#E9004A'}`, borderRadius: '50%' }} />
              <div>{elapsedTime}</div>
            </Box>
          )
        },
      },
      { field: 'addedByName' as keyof LinkedinProfile, headerName: 'Added By', width: '150px', sortable: true, type: 'text' },
      {
        field: 'actions' as keyof LinkedinProfile | 'actions',
        headerName: '',
        width: '80px',
        sortable: false,
        renderCell: (row: LinkedinProfile) => {
          const profileUrl = row.profileUrl?.startsWith('http') ? row.profileUrl : `https://${row.profileUrl}`;
          return (
            <Box display="flex" gap="16px">
              <a
                href={profileUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'none' }}
                onClick={(e) => {
                  e.preventDefault();
                  window.open(profileUrl, '_blank');
                }}
              >
                <LinkedinIcon />
              </a>
              {hasPermission(PERMISSION_IDS.LINKEDIN_ADD_APPLICANT) && (
                <Button
                  variant='contained'
                  onClick={() => handleAddApplicant(row)}
                  disabled={!!row.applicantId}
                  style={{ height: '20px', padding: '4px 8px', fontSize: '12px', width: '102px' }}
                >
                  Add Applicant
                </Button>
              )}
            </Box>
          );
        },
      },
    ], [isOnlyWithEmail, hasPermission, handleAddApplicant]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  return (
    <Box className="page-container">
      <Typography className="page-title">
        Linkedin Search
      </Typography>
      <Box className="search-section">
        <Box className="search-bar">
          <div style={{ flex: 1 }}>
            <TextField
              placeholder="Name"
              value={searchParams.name}
              onChange={(e) => setSearchParams({ ...searchParams, name: e.target.value })}
              width="100%"
            />
          </div>
          <div style={{ flex: 1 }}>
            <TextField
              placeholder="Skills"
              value={searchParams.skills}
              onChange={(e) => setSearchParams({ ...searchParams, skills: e.target.value })}
              width="100%"
            />
          </div>
          <div style={{ flex: 1 }}>
            <TextField
              placeholder="Company"
              value={searchParams.company}
              onChange={(e) => setSearchParams({ ...searchParams, company: e.target.value })}
              width="100%"
            />
          </div>
          <div style={{ flex: 1 }}>
            <SelectField
              placeholder="Experience"
              value={searchParams.experience}
              onChange={(e) => setSearchParams({ ...searchParams, experience: e.target.value as string })}
              options={[
                { value: '12', label: '1 + Years' },
                { value: '24', label: '2 + Years' },
                { value: '36', label: '3 + Years' },
                { value: '60', label: '5 + Years' },
              ]}
              width="100%"
            />
          </div>
          <div style={{ flex: 1 }}>
            <TextField
              placeholder="Vacancy"
              value={searchParams.vacancy}
              onChange={(e) => setSearchParams({ ...searchParams, vacancy: e.target.value })}
              width="100%"
            />
          </div>
          <div style={{ flex: 1 }}>
            <TextField
              placeholder="Email"
              value={searchParams.email}
              onChange={(e) => setSearchParams({ ...searchParams, email: e.target.value })}
              width="100%"
            />
          </div>
          <div style={{ flex: 1 }}>
            <TextField
              placeholder="Country"
              value={searchParams.country}
              onChange={(e) => setSearchParams({ ...searchParams, country: e.target.value })}
              width="100%"
            />
          </div>
          <div style={{ flex: 1 }}>
            <TextField
              placeholder="Added By"
              value={searchParams.added_by}
              onChange={(e) => setSearchParams({ ...searchParams, added_by: e.target.value })}
              width="100%"
            />
          </div>
          <Box className="search-bar-button-group">
            <Button variant="contained" onClick={handleSearch} width="120px">
              Search
            </Button>
            <Button variant="outlined" onClick={handleReset} width="120px">
              Clear
            </Button>
          </Box>
        </Box>
        {isLoading ? (
          <Loading message="Loading LinkedIn profiles..." />
        ) : (
          <CustomTable
            columns={columns}
            rows={profiles}
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            onSort={handleSort}
            sortField={sortField}
            sortDirection={sortDirection}
          />
        )}
      </Box>

      <ProfileDetailsPopup
        open={open}
        onClose={() => {
          setOpen(false);
          setSelectedProfileId(null);
        }}
        profileId={selectedProfileId || 0}
      />
    </Box>
  );
};

export default LinkedinSearch;
